import React, { useState } from "react";
import { motion } from "framer-motion";
import { Volume2, VolumeX, ChevronDown } from "lucide-react";
import { TEASER } from "../lib/site";
import { useSite } from "../context/SiteContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export const Hero = () => {
  const site = useSite();
  const videoId = site?.featured_video_id || TEASER.youtubeId;
  const [muted, setMuted] = useState(true);
  const origin = typeof window !== "undefined" ? window.location.origin : "";

  const rawCover = site?.images?.videoCover;
  const poster = rawCover
    ? (rawCover.startsWith("http") ? rawCover : `${BACKEND}${rawCover}`)
    : `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;

  const src =
    `https://www.youtube.com/embed/${videoId}` +
    `?autoplay=1&mute=${muted ? 1 : 0}&loop=1&playlist=${videoId}` +
    `&controls=0&disablekb=1&fs=0&modestbranding=1&rel=0&iv_load_policy=3&playsinline=1` +
    (origin ? `&origin=${encodeURIComponent(origin)}` : "");

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative w-full h-[100svh] min-h-[560px] overflow-hidden bg-black"
    >
      {/* Poster behind the video (instant paint + fallback) */}
      <img
        src={poster}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover"
        onError={(e) => {
          if (!e.currentTarget.src.includes("hqdefault")) {
            e.currentTarget.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
          }
        }}
      />

      {/* Full-screen cinematic background video (object-cover behaviour) */}
      <div className="absolute inset-0 overflow-hidden">
        <iframe
          key={muted ? "muted" : "unmuted"}
          data-testid="hero-video-iframe"
          title={`Akalibe · ${TEASER.title}`}
          src={src}
          allow="autoplay; encrypted-media; picture-in-picture"
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none border-0"
          style={{ width: "100vw", height: "56.25vw", minHeight: "100svh", minWidth: "177.78svh" }}
        />
      </div>

      {/* Subtle readability veil (kept minimal — video stays dominant) */}
      <div className="absolute inset-0 bg-black/25" />
      <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-black/60 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-base via-base/40 to-transparent" />

      {/* Artist wordmark overlay */}
      <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center pointer-events-none">
        <motion.span
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.5 }}
          className="text-[11px] sm:text-xs uppercase tracking-[0.5em] text-gold/90 mb-5"
        >
          Indie Pop · R&B · Lyon
        </motion.span>
        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, delay: 0.7, ease: [0.76, 0, 0.24, 1] }}
          className="font-serif font-light gold-text text-shadow-gold leading-none tracking-[0.18em] text-5xl sm:text-6xl md:text-7xl lg:text-8xl"
        >
          AKALIBE
        </motion.h1>
      </div>

      {/* Discreet sound toggle */}
      <button
        data-testid="hero-sound-toggle"
        onClick={() => setMuted((m) => !m)}
        aria-label={muted ? "Activer le son" : "Couper le son"}
        className="absolute bottom-6 right-5 sm:right-8 z-10 inline-flex items-center justify-center w-11 h-11 rounded-full border border-white/30 text-white/90 bg-black/30 backdrop-blur-sm hover:border-gold hover:text-gold transition-colors duration-300"
      >
        {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
      </button>

      {/* Scroll cue */}
      <motion.a
        href="#stats"
        data-testid="hero-scroll-cue"
        aria-label="Défiler vers le bas"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 text-white/70 hover:text-gold transition-colors"
      >
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
          className="block"
        >
          <ChevronDown className="w-6 h-6" />
        </motion.span>
      </motion.a>
    </section>
  );
};
